<script setup lang="ts">
import type { Ref } from "vue";
import { ref, onMounted, watch } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomCard,
  AtomText,
  AtomButton,
  AtomLink,
  MolModal,
  MolCollapseIndicator,
  AtomCheckbox,
  useTogglable,
} from "@chatfood/design-system";
import dayjs from "dayjs";
import { useLocale } from "~/composables/use-locale";
import { useCustomerStore } from "~/stores/customer";
import { useAnalytics, useCookie } from "#imports";
import { useBusinessStore } from "~/stores/business";
import { DELIVERECT_PRIVACY_POLICY } from "~/domain/constants";

type IUserCookiesConsent = {
  acceptedPerformance: boolean;
  acceptedTargeting: boolean;
  acceptedSocialMedia: boolean;
  acceptedFunctional: boolean;
  createdAt: Date;
};

const COOKIE_NAME = "cookieConsentSettings";

const css = bemBuilder("cookies-consent");
const { t } = useLocale();
const { enableGoogleAnalytics, enableMixPanel, releaseQueue, enableSnowplow } =
  useAnalytics();

const customer = useCustomerStore();
const business = useBusinessStore();

const IN_ONE_MONTH = dayjs().add(1, "month").toDate();
const cookieSettings = useCookie<IUserCookiesConsent | undefined>(COOKIE_NAME, {
  expires: IN_ONE_MONTH,
});

const {
  state: isModalVisible,
  enable: showModal,
  disable: hideModal,
} = useTogglable();
const {
  state: isBubbleVisible,
  enable: showBubble,
  disable: hideBubble,
} = useTogglable();

const isPerformanceChecked = ref(false);
const isTargetingChecked = ref(false);
const isSocialMediaChecked = ref(false);
const isFunctionalChecked = ref(false);

const showPerformanceDescription = ref(false);
const showTargetingDescription = ref(false);
const showNecessaryDescription = ref(false);
const showSocialMediaDescription = ref(false);
const showFunctionalDescription = ref(false);

const resolveAnalytics = () => {
  if (
    cookieSettings.value?.acceptedPerformance &&
    cookieSettings.value?.acceptedTargeting &&
    business.gtmContainerId
  ) {
    enableGoogleAnalytics({ containerId: business.gtmContainerId });
  }

  if (cookieSettings.value?.acceptedPerformance) {
    enableMixPanel();
    enableSnowplow();
  }

  releaseQueue();
};

const onConfirmed = () => {
  resolveAnalytics();
  hideModal();
  hideBubble();
};

const savePreferences = (opts: IUserCookiesConsent) => {
  cookieSettings.value = opts;

  onConfirmed();
};

const acceptAllCookies = () => {
  cookieSettings.value = {
    acceptedPerformance: true,
    acceptedTargeting: true,
    acceptedSocialMedia: true,
    acceptedFunctional: true,
    createdAt: new Date(),
  };

  onConfirmed();
};

const rejectAllCookies = () => {
  cookieSettings.value = {
    acceptedPerformance: false,
    acceptedTargeting: false,
    acceptedSocialMedia: false,
    acceptedFunctional: false,
    createdAt: new Date(),
  };

  onConfirmed();
};

const toggleRef = (toggle: Ref<boolean>) => {
  toggle.value = !toggle.value;
};

const settingOptions = [
  {
    id: "performance",
    title: t("components.cookies_consent.settings.performance_title"),
    description: t(
      "components.cookies_consent.settings.performance_description",
    ),
    isExpanded: showPerformanceDescription,
    toggleDescription: () => toggleRef(showPerformanceDescription),
    isChecked: isPerformanceChecked,
    toggleCheck: () => toggleRef(isPerformanceChecked),
  },
  {
    id: "targeting",
    title: t("components.cookies_consent.settings.targeting_title"),
    description: t("components.cookies_consent.settings.targeting_description"),
    isExpanded: showTargetingDescription,
    toggleDescription: () => toggleRef(showTargetingDescription),
    isChecked: isTargetingChecked,
    toggleCheck: () => toggleRef(isTargetingChecked),
  },
  {
    id: "social_media",
    title: t("components.cookies_consent.settings.social_media_title"),
    description: t(
      "components.cookies_consent.settings.social_media_description",
    ),
    isExpanded: showSocialMediaDescription,
    toggleDescription: () => toggleRef(showSocialMediaDescription),
    isChecked: isSocialMediaChecked,
    toggleCheck: () => toggleRef(isSocialMediaChecked),
  },
  {
    id: "functional",
    title: t("components.cookies_consent.settings.functional_title"),
    description: t(
      "components.cookies_consent.settings.functional_description",
    ),
    isExpanded: showFunctionalDescription,
    toggleDescription: () => toggleRef(showFunctionalDescription),
    isChecked: isFunctionalChecked,
    toggleCheck: () => toggleRef(isFunctionalChecked),
  },
  {
    id: "necessary",
    title: t("components.cookies_consent.settings.strictly_necessary_title"),
    description: t(
      "components.cookies_consent.settings.strictly_necessary_description",
    ),
    isExpanded: showNecessaryDescription,
    toggleDescription: () => toggleRef(showNecessaryDescription),
    isChecked: ref(),
    toggleCheck: () => {},
  },
];

watch(
  () => customer.isLogged,
  (value, previous) => {
    if (!value && previous) {
      cookieSettings.value = undefined;
    }
  },
);

onMounted(() => {
  if (!cookieSettings.value) {
    showBubble();
    return;
  }

  resolveAnalytics();
});
</script>

<template>
  <div :class="css()">
    <Transition name="fade" mode="out-in">
      <AtomCard
        v-if="isBubbleVisible"
        :class="css('consent-card')"
        padding="large"
        radii="medium"
        elevation="large"
      >
        <div :class="css('consent-info')">
          <AtomText color="color-text-body" type="h3">
            {{ t("components.cookies_consent.title") }}
          </AtomText>
          <AtomText color="color-text-secondary">
            {{ t("components.cookies_consent.text") }}
          </AtomText>
          <AtomLink
            :to="business.privacyPolicyCookieLink ?? DELIVERECT_PRIVACY_POLICY"
            color="color-text-secondary"
            target="_blank"
            data-test="consent-info-privacy-policy-link"
          >
            {{ t("components.cookies_consent.privacy_and_cookie_policy") }}
          </AtomLink>
        </div>
        <div :class="css('bubble-actions')">
          <AtomButton
            data-test="accept-all"
            size="large"
            theme="tailored"
            @click="acceptAllCookies()"
          >
            {{ t("components.cookies_consent.cta") }}
          </AtomButton>
          <AtomButton
            data-test="reject-all-bubble"
            size="large"
            theme="tailored"
            @click="rejectAllCookies()"
          >
            {{ t("components.cookies_consent.settings.reject_cta") }}
          </AtomButton>
        </div>
        <AtomLink
          to=""
          color="color-text-secondary"
          data-test="show-modal"
          @click.prevent="showModal()"
        >
          {{ t("components.cookies_consent.settings_cta") }}
        </AtomLink>
      </AtomCard>
    </Transition>
    <MolModal
      :model-value="isModalVisible"
      :width-fit-content="true"
      :show-close="true"
      @close="hideModal"
    >
      <div :class="css('settings-modal')">
        <div :class="css('settings-header')">
          <AtomText
            :class="css('settings-title')"
            color="color-text-body"
            type="h3"
          >
            {{ t("components.cookies_consent.settings.title") }}
          </AtomText>
          <AtomText color="color-text-secondary">
            {{ t("components.cookies_consent.settings.text") }}
          </AtomText>
          <AtomLink
            :to="business.privacyPolicyCookieLink ?? DELIVERECT_PRIVACY_POLICY"
            color="color-text-secondary"
            target="_blank"
            data-test="settings-header-privacy-policy-link"
          >
            {{
              t("components.cookies_consent.settings.privacy_and_cookie_policy")
            }}
          </AtomLink>
        </div>
        <div :class="css('settings-content')">
          <div
            v-for="option in settingOptions"
            :key="option.id"
            :class="css('collapse-item')"
          >
            <div :class="css('collapse-header')">
              <AtomText
                :class="css('collapse-name')"
                type="body-bold"
                color="color-text-body"
                :data-test="`${option.id}-cta`"
                @click="option.toggleDescription"
              >
                <MolCollapseIndicator
                  theme="ghost"
                  variant="primary"
                  :collapsed="!option.isExpanded.value"
                />
                {{ option.title }}
              </AtomText>
              <AtomCheckbox
                v-if="option.isChecked.value !== undefined"
                :checked="option.isChecked.value"
                :data-test="`${option.id}-checkbox`"
                @change="option.toggleCheck"
              />
              <AtomText v-else type="small-bold" color="color-primary">
                {{ t("components.cookies_consent.settings.always_active") }}
              </AtomText>
            </div>
            <AtomText
              v-show="option.isExpanded.value"
              type="small"
              color="color-text-secondary"
              tag="div"
              :class="css('collapse-description')"
              :data-test="`${option.id}-description`"
            >
              {{ option.description }}
            </AtomText>
          </div>
        </div>
        <div :class="css('settings-cta')">
          <AtomButton
            size="large"
            theme="tailored"
            data-test="save-settings"
            @click="
              savePreferences({
                acceptedPerformance: isPerformanceChecked,
                acceptedTargeting: isTargetingChecked,
                acceptedSocialMedia: isSocialMediaChecked,
                acceptedFunctional: isFunctionalChecked,
                createdAt: new Date(),
              })
            "
          >
            {{ t("components.cookies_consent.settings.accept_cta") }}
          </AtomButton>
          <AtomButton
            size="large"
            theme="tailored"
            data-test="reject-all"
            @click="rejectAllCookies()"
          >
            {{ t("components.cookies_consent.settings.reject_cta") }}
          </AtomButton>
        </div>
      </div>
    </MolModal>
  </div>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.cookies-consent {
  display: flex;

  &__consent-card {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-lg);
    position: fixed;
    z-index: 10;
    right: 0;
    left: 0;
    margin: auto var(--spacing-lg);
    bottom: var(--spacing-lg);

    @media (width > $breakpoint-sm) {
      bottom: var(--spacing-2xl);
      max-width: 375px;
      right: var(--spacing-2xl);
      margin: initial;
      left: initial;
    }
  }

  &__consent-info {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__settings-modal {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-lg);
    max-width: 375px;
    z-index: 12;
    width: 100%;
  }

  &__settings-header {
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__settings-content {
    max-height: 400px;
    overflow: auto;
    width: 100%;
    gap: var(--spacing-lg);
    display: flex;
    flex-direction: column;
  }

  &__settings-title {
    display: flex;
    margin-bottom: var(--spacing-sm);
    justify-content: center;
  }

  &__settings-cta {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: row;
    align-items: stretch;
    width: 100%;

    > * {
      flex: 1;
      min-width: 0;
    }
  }

  &__bubble-actions {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: row;
    align-items: stretch;
    width: 100%;

    > * {
      flex: 1;
      min-width: 0;
    }
  }

  &__collapse-item {
    width: 100%;
    display: flex;
    flex-direction: column;
  }

  &__collapse-header {
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-items: center;
  }

  &__collapse-name {
    display: flex;
    gap: var(--spacing-md);
    cursor: pointer;
  }

  &__collapse-description {
    margin-top: var(--spacing-sm);
    padding-left: var(--spacing-2xl);
  }
}
</style>
